/**
 * Parsing History Page
 * 
 * View history of all parsing tasks and their results
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Descriptions,
  Alert,
  Progress,
  Drawer,
  Popconfirm,
  Tooltip,
} from 'antd'
import {
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  DownloadOutlined,
  ProjectOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import api from '@/services/api'
import { getRegionName } from '@/utils/regions'

const { Text } = Typography

interface ParsingTask {
  task_id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'uploaded'
  crm_id: number
  project_id?: number
  project_name?: string
  main_query: string
  region_code: string
  progress: number
  current_step?: string
  error_message?: string
  task_metadata?: {
    min_clicks_threshold?: number
    download_all_queries?: boolean
    top_queries_count?: number
  }
}

interface ParsingResult {
  success: boolean
  main_query: string
  region_code: string
  region_name: string
  domains: any[]
  urls: any[]
  queries: any[]
  domains_count: number
  urls_count: number
  queries_count: number
  started_at: string
  completed_at: string
  duration_seconds: number
}

const statusColors: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
  uploaded: 'cyan',
}

const statusIcons: Record<string, any> = {
  pending: <ClockCircleOutlined />,
  running: <RocketOutlined spin />,
  completed: <CheckCircleOutlined />,
  uploaded: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
}

export default function ParsingHistoryPage() {
  const [tasks, setTasks] = useState<ParsingTask[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedTask, setSelectedTask] = useState<ParsingTask | null>(null)
  const [result, setResult] = useState<ParsingResult | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [loadingResult, setLoadingResult] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<string>('')
  const [hasRunningTasks, setHasRunningTasks] = useState(false)

  useEffect(() => {
    loadTasks()
    
    // * Auto-refresh every 5 seconds if there are running tasks
    const interval = setInterval(() => {
      if (hasRunningTasks) {
        console.log('🔄 Auto-refreshing tasks (running tasks detected)...')
        loadTasks()
      }
    }, 5000)  // * Increased frequency to 5 seconds
    
    return () => clearInterval(interval)
  }, [hasRunningTasks])  // * Fixed: only depend on hasRunningTasks flag

  const loadTasks = async () => {
    try {
      setLoading(true)
      
      // * Call through backend API
      const data = await api.getParsingTasks()
      
      console.log('📊 Parsing tasks loaded:', data)
      
      const tasksList = data.tasks || []
      setTasks(tasksList)
      
      // * Check if there are running tasks
      const hasRunning = tasksList.some((t: ParsingTask) => 
        t.status === 'running' || t.status === 'pending'
      )
      setHasRunningTasks(hasRunning)
      
      console.log(`✅ Loaded ${tasksList.length} tasks, running: ${hasRunning}`)
      
    } catch (error: any) {
      console.error('❌ Error loading tasks:', error)
      console.error('Error details:', error.response?.data || error.message)
      
      // * Don't show error on first load
      if (tasks.length === 0) {
        console.log('ℹ️ No tasks yet (first load)')
      } else {
        message.error('Ошибка загрузки задач')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadResult = async (task: ParsingTask) => {
    try {
      setLoadingResult(true)
      setSelectedTask(task)
      setDrawerVisible(true)
      
      const resultData = await api.getParsingResult(task.task_id)
      setResult(resultData)
    } catch (error: any) {
      console.error('❌ Error loading result:', error)
      
      // * Better error handling for 404
      if (error.response?.status === 404) {
        message.error('Результаты парсинга не найдены. Возможно, они еще не готовы или были удалены.')
      } else if (error.response?.status === 500) {
        message.error('Ошибка сервера при загрузке результатов. Попробуйте позже.')
      } else {
        message.error('Ошибка загрузки результатов: ' + (error.response?.data?.detail || error.message || 'Неизвестная ошибка'))
      }
      
      setResult(null)
    } finally {
      setLoadingResult(false)
    }
  }

  const handleCancelTask = async (task: ParsingTask) => {
    try {
      console.log(`🗑️ Cancelling task: ${task.task_id}`)
      
      // * Clear from parsing store (localStorage)
      if (task.project_id) {
        const { useParsingStore } = await import('@/stores/parsingStore')
        const clearTask = useParsingStore.getState().clearTask
        clearTask(task.project_id)
      }
      
      // * TODO: Add API endpoint to cancel/delete task from backend
      // await api.cancelParsingTask(task.task_id)
      
      message.success('Задача отменена')
      
      // * Reload tasks list
      await loadTasks()
      
    } catch (error: any) {
      console.error('Error cancelling task:', error)
      message.error('Ошибка отмены задачи: ' + (error.message || 'Неизвестная ошибка'))
    }
  }

  const handleUploadToVDL = async () => {
    if (!selectedTask || !result) return

    try {
      setUploading(true)
      setUploadProgress(0)
      setUploadStatus('Запуск загрузки...')
      
      const response = await api.processParsingResults(
        selectedTask.task_id,
        selectedTask.crm_id
      )
      
      if (response.success && response.upload_task_id) {
        const uploadTaskId = response.upload_task_id
        message.info('Загрузка в VDL запущена...')
        
        // * Poll upload status
        const pollInterval = setInterval(async () => {
          try {
            const status = await api.getUploadStatus(uploadTaskId)
            
            setUploadProgress(status.progress || 0)
            setUploadStatus(status.current_step || status.status)
            
            if (status.status === 'completed') {
              clearInterval(pollInterval)
              message.success(
                `✅ Загружено ${status.domains_uploaded} источников в VDL!`
              )
              setUploading(false)
              setDrawerVisible(false)
            } else if (status.status === 'failed') {
              clearInterval(pollInterval)
              message.error('Ошибка загрузки: ' + (status.error_message || 'Неизвестная ошибка'))
              setUploading(false)
            }
          } catch (error: any) {
            console.error('Error polling upload status:', error)
          }
        }, 2000)
        
        // * Auto-stop after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval)
          if (uploading) {
            setUploading(false)
          }
        }, 300000)
      } else {
        throw new Error(response.error || 'Не получен upload_task_id')
      }
    } catch (error: any) {
      message.error('Ошибка запуска загрузки: ' + (error.message || 'Неизвестная ошибка'))
      setUploading(false)
    }
  }

  const columns: ColumnsType<ParsingTask> = [
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: ParsingTask) => {
        // * Show error tooltip for failed tasks
        if (status === 'failed' && record.error_message) {
          return (
            <Tooltip 
              title={
                <div style={{ maxWidth: 300 }}>
                  <Text style={{ color: 'white' }}><strong>Ошибка:</strong></Text>
                  <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.85)' }}>
                    {record.error_message}
                  </div>
                </div>
              }
              placement="topLeft"
            >
              <Tag icon={statusIcons[status]} color={statusColors[status]} style={{ cursor: 'help' }}>
                {status.toUpperCase()}
              </Tag>
            </Tooltip>
          )
        }
        
        return (
          <Tag icon={statusIcons[status]} color={statusColors[status]}>
            {status.toUpperCase()}
          </Tag>
        )
      },
    },
    {
      title: 'Запрос',
      dataIndex: 'main_query',
      key: 'main_query',
      ellipsis: true,
    },
    {
      title: 'Регион',
      dataIndex: 'region_code',
      key: 'region_code',
      width: 250,
      ellipsis: true,
      render: (regionCode: string) => (
        <span title={getRegionName(regionCode)}>
          {getRegionName(regionCode)}
        </span>
      ),
    },
    {
      title: 'Параметры парсера',
      key: 'parser_params',
      width: 220,
      render: (_: any, record: ParsingTask) => {
        const metadata = record.task_metadata
        if (!metadata || (!metadata.min_clicks_threshold && metadata.download_all_queries === undefined)) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <span style={{ opacity: 0.5 }}>По умолчанию</span>
            </Text>
          )
        }
        
        return (
          <Space direction="vertical" size={6} style={{ fontSize: 12, width: '100%' }}>
            {metadata.min_clicks_threshold !== undefined && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                padding: '4px 8px',
                background: metadata.min_clicks_threshold === 0 ? '#fff7e6' : '#e6f7ff',
                borderRadius: 4,
                border: `1px solid ${metadata.min_clicks_threshold === 0 ? '#ffd591' : '#91d5ff'}`
              }}>
                <span style={{ fontSize: 11, color: '#666' }}>🔽 Минимум кликов:</span>
                <Tag 
                  color={metadata.min_clicks_threshold === 0 ? 'warning' : 'blue'} 
                  style={{ margin: 0, fontSize: 11, fontWeight: 600 }}
                >
                  {metadata.min_clicks_threshold === 0 ? 'Без фильтра' : `${metadata.min_clicks_threshold}+`}
                </Tag>
              </div>
            )}
            {metadata.download_all_queries !== undefined && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                padding: '4px 8px',
                background: metadata.download_all_queries ? '#fff1f0' : '#f6ffed',
                borderRadius: 4,
                border: `1px solid ${metadata.download_all_queries ? '#ffccc7' : '#b7eb8f'}`
              }}>
                <span style={{ fontSize: 11, color: '#666' }}>📥 Режим:</span>
                <Tag 
                  color={metadata.download_all_queries ? 'red' : 'green'} 
                  style={{ margin: 0, fontSize: 11, fontWeight: 600 }}
                >
                  {metadata.download_all_queries ? 'Все запросы' : `ТОП-${metadata.top_queries_count || 100}`}
                </Tag>
              </div>
            )}
            {!metadata.download_all_queries && metadata.top_queries_count !== undefined && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                padding: '4px 8px',
                background: '#e6f7ff',
                borderRadius: 4,
                border: '1px solid #91d5ff'
              }}>
                <span style={{ fontSize: 11, color: '#666' }}>📊 ТОП-N:</span>
                <Tag color="cyan" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                  {metadata.top_queries_count} запросов
                </Tag>
              </div>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Проект',
      dataIndex: 'crm_id',
      key: 'crm_id',
      width: 300,
      ellipsis: true,
      render: (crmId: number, record: ParsingTask) => {
        const projectTitle = record.project_name 
          ? `${record.project_name} (ID: ${crmId})`
          : `Проект ID: ${crmId}`
        
        // * Link to project sources page using crm_id
        const projectLink = crmId
          ? `/projects/${crmId}/sources`
          : '/projects'
        
        return (
          <Link to={projectLink} title={projectTitle}>
            <Button type="link" size="small" icon={<ProjectOutlined />}>
              {record.project_name || `Проект ${crmId}`}
              <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>
                (ID: {crmId})
              </Text>
            </Button>
          </Link>
        )
      },
    },
    {
      title: 'Прогресс',
      dataIndex: 'progress',
      key: 'progress',
      width: 200,
      render: (progress: number, record: ParsingTask) => {
        if (record.status === 'completed') {
          return <Progress percent={100} size="small" status="success" />
        }
        if (record.status === 'failed') {
          return (
            <Tooltip title={record.error_message || 'Ошибка парсинга'}>
              <Progress percent={progress || 100} size="small" status="exception" />
            </Tooltip>
          )
        }
        
        // * Show current step for pending/running tasks
        if ((record.status === 'pending' || record.status === 'running') && record.current_step) {
          return (
            <div>
              <Progress percent={progress || 0} size="small" status="active" />
              <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                {record.current_step}
              </Text>
            </div>
          )
        }
        
        return <Progress percent={progress || 0} size="small" status="active" />
      },
    },
    {
      title: 'Task ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 200,
      ellipsis: true,
      render: (taskId: string) => (
        <Text copyable style={{ fontSize: 11, fontFamily: 'monospace' }}>
          {taskId.substring(0, 8)}...
        </Text>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: ParsingTask) => (
        <Space size="small">
          {/* * Show results for completed tasks */}
          {record.status === 'completed' && (
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => loadResult(record)}
            >
              Результаты
            </Button>
          )}
          
          {/* * Show error details for failed tasks */}
          {record.status === 'failed' && record.error_message && (
            <Tooltip title={record.error_message} placement="topRight">
              <Button
                type="default"
                size="small"
                danger
                icon={<CloseCircleOutlined />}
              >
                Ошибка
              </Button>
            </Tooltip>
          )}
          
          {/* * Cancel button for pending/failed tasks */}
          {(record.status === 'pending' || record.status === 'failed') && (
            <Popconfirm
              title="Отменить эту задачу?"
              description={
                <div>
                  <p>Задача будет удалена из списка.</p>
                  {record.status === 'pending' && (
                    <p style={{ color: '#faad14', marginTop: 8 }}>
                      ⚠️ Если задача выполняется в фоне, она не остановится.
                    </p>
                  )}
                </div>
              }
              onConfirm={() => handleCancelTask(record)}
              okText="Да, отменить"
              cancelText="Нет"
              okType="danger"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                title="Отменить задачу"
              >
                Отменить
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadTasks}
            loading={loading}
          >
            Обновить
          </Button>
        </div>

        <Card>
          {tasks.length === 0 && !loading && (
            <Alert
              message="Нет задач парсинга"
              description={
                <div>
                  <p>Парсинги будут отображаться здесь после запуска.</p>
                  <p><strong>Как запустить:</strong></p>
                  <ol>
                    <li>Перейдите в <strong>Проекты</strong></li>
                    <li>Нажмите кнопку <strong>"Yandex"</strong> 🚀 на проекте</li>
                    <li>Запустите парсинг</li>
                  </ol>
                  <p style={{ marginTop: 12 }}>
                    <Text type="secondary">Debug: Loaded {tasks.length} tasks from API</Text>
                  </p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Table
            columns={columns}
            dataSource={tasks}
            loading={loading}
            rowKey="task_id"
            size="small"
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Всего: ${total} задач`,
            }}
          />
        </Card>
      </Space>

      {/* Results Drawer */}
      <Drawer
        title={
          <Space>
            <EyeOutlined />
            <span>Результаты парсинга</span>
          </Space>
        }
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setSelectedTask(null)
          setResult(null)
        }}
        width={900}
        footer={
          result && (
            <Space direction="vertical" style={{ width: '100%' }}>
              {uploading && (
                <Progress 
                  percent={Math.round(uploadProgress)} 
                  status={uploadProgress === 100 ? 'success' : 'active'}
                  format={() => `${Math.round(uploadProgress)}%`}
                />
              )}
              {uploadStatus && (
                <Text type="secondary">{uploadStatus}</Text>
              )}
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleUploadToVDL}
                  loading={uploading}
                  disabled={uploading}
                  size="large"
                >
                  {uploading ? 'Загрузка...' : 'Загрузить в VDL'}
                </Button>
                <Button
                  onClick={() => setDrawerVisible(false)}
                  disabled={uploading}
                >
                  Закрыть
                </Button>
              </Space>
            </Space>
          )
        }
      >
        {loadingResult ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Progress type="circle" percent={50} status="active" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Загрузка результатов...</Text>
            </div>
          </div>
        ) : result ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Summary */}
            <Alert
              message="Парсинг завершен успешно"
              description={
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Запрос">
                    <Text strong>{result.main_query}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Регион">
                    <Text>{result.region_name || getRegionName(result.region_code)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Время">
                    <Text>{result.duration_seconds} секунд</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Завершено">
                    <Text>{dayjs(result.completed_at).format('DD.MM.YYYY HH:mm')}</Text>
                  </Descriptions.Item>
                </Descriptions>
              }
              type="success"
              showIcon
            />

            {/* Statistics */}
            <Card size="small" title="📊 Статистика">
              <Space size="large">
                <div>
                  <Text type="secondary">Доменов:</Text>
                  <br />
                  <Text strong style={{ fontSize: 24, color: '#1890ff' }}>
                    {result.domains_count}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">URL:</Text>
                  <br />
                  <Text strong style={{ fontSize: 24, color: '#52c41a' }}>
                    {result.urls_count}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">Запросов:</Text>
                  <br />
                  <Text strong style={{ fontSize: 24, color: '#faad14' }}>
                    {result.queries_count}
                  </Text>
                </div>
              </Space>
            </Card>

            {/* Top Domains */}
            <Card size="small" title="🏆 ТОП-10 доменов">
              <Table
                dataSource={result.domains?.slice(0, 10) || []}
                columns={[
                  {
                    title: '#',
                    key: 'index',
                    width: 50,
                    render: (_: any, __: any, index: number) => index + 1,
                  },
                  {
                    title: 'Домен',
                    dataIndex: 'host',
                    key: 'host',
                    ellipsis: true,
                  },
                  {
                    title: 'Клики',
                    dataIndex: 'total_clicks',
                    key: 'total_clicks',
                    width: 100,
                    render: (value: number) => value?.toLocaleString() || 0,
                  },
                  {
                    title: 'Запросов',
                    dataIndex: 'queries_count',
                    key: 'queries_count',
                    width: 100,
                  },
                  {
                    title: 'Позиция',
                    dataIndex: 'average_position',
                    key: 'average_position',
                    width: 100,
                    render: (value: number) => value?.toFixed(1) || '0.0',
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="host"
              />
            </Card>

            {/* Top URLs */}
            <Card size="small" title="🔗 ТОП-10 URL">
              <Table
                dataSource={result.urls?.slice(0, 10) || []}
                columns={[
                  {
                    title: '#',
                    key: 'index',
                    width: 50,
                    render: (_: any, __: any, index: number) => index + 1,
                  },
                  {
                    title: 'URL',
                    dataIndex: 'url',
                    key: 'url',
                    ellipsis: true,
                    render: (url: string) => (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {url}
                      </a>
                    ),
                  },
                  {
                    title: 'Запросов',
                    dataIndex: 'queries',
                    key: 'queries',
                    width: 100,
                  },
                  {
                    title: 'Позиция',
                    dataIndex: 'average_position',
                    key: 'average_position',
                    width: 100,
                    render: (value: number) => value?.toFixed(1) || '0.0',
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="url"
              />
            </Card>
          </Space>
        ) : (
          <Alert
            message="Результаты недоступны"
            description="Не удалось загрузить результаты парсинга"
            type="warning"
            showIcon
          />
        )}
      </Drawer>
    </div>
  )
}

